/* @flow */
import Vue from "vue";
import axios from "axios";
import normalize from "jsonapi-normalizer";
import oPath from "object-path";

import { REMOVE_SESSION, UPDATE_SESSION } from "./session";
import { NEW_NOTIFICATION, REPLACE_NOTIFICATION } from "./notifications";
import type { JsonApiEntity } from "@/store";
import { computePaginateSortFilterParams } from "@/utils/api";

import {
  passwordExpirationOptions,
  sessionExpirationOptions,
} from "@/utils/api";

export const REPLACE_USERS = "REPLACE_USERS";
export const RESET_STATE = "RESET_STATE";
export const RESET_USERS = "RESET_USERS";
export const ADD_USERS = "ADD_USERS";
export const MODIFY_USER = "MODIFY_USER";
export const REMOVE_USER = "REMOVE_USER";
export const START_USER_FETCHING = "START_USER_FETCHING";
export const DONE_USER_FETCHING = "DONE_USER_FETCHING";
export const SET_USERS_TOTAL = "SET_USERS_TOTAL";
export const UPDATE_PAGINATION = "UPDATE_PAGINATION";

export const FETCH_USERS = "FETCH_USERS";
export const FETCH_USER = "FETCH_USER";
export const FETCH_CURRENT_USER = "FETCH_CURRENT_USER";
export const CREATE_USER = "CREATE_USER";
export const UPDATE_USER = "UPDATE_USER";
export const DELETE_USER = "DELETE_USER";
export const USER_FORGOT_PASSWORD = "USER_FORGOT_PASSWORD";
export const USER_RESET_PASSWORD = "USER_RESET_PASSWORD";
export const VERIFY_NEW_USER = "VERIFY_NEW_USER";
export const RESET_USER_CREDENTIALS = "RESET_USER_CREDENTIALS";

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
  pagParams: {
    pagination: {},
    startRow: 0,
    endRow: 100,
    sortModel: [],
    filterModel: {},
  },
  fetch_status: {
    all: "",
  },
});

export default {
  state: initialState(),
  getters: {
    userDefaults: () => ({
      first_name: "",
      last_name: "",
      email: "",
      new_password: "",
      session_timeout_seconds: sessionExpirationOptions.find(
        (f) => f.text === "1 hour"
      ).value,
      password_lifetime_seconds: passwordExpirationOptions.find(
        (f) => f.text === "3 months"
      ).value,
    }),
    //$FlowFixMe
    users: ({ entities = {} }: Object) =>
      Object.keys(entities).map((key) => entities[key]),
    userById: (state: Object) => (id: string) => state.entities[id] || {},
    userByEmail: (state: Object, getters: Object) => (email: string) =>
      getters.users.find((user) => user.email === email) || {},
  },
  mutations: {
    [RESET_STATE](state: Object) {
      Object.assign(state, initialState());
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities;
    },
    [START_USER_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = "loading";
    },
    [DONE_USER_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = "loaded";
    },
    [REPLACE_USERS](state: Object, { entities = {} }: Object) {
      state.entities = entities;
      const iss = initialState();
      state.pagParams = iss.pagParams;
    },
    [ADD_USERS](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      };
      state.statistics.total = Object.keys(state.entities).length;
    },
    [MODIFY_USER](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      };
    },
    [REMOVE_USER](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id);
    },
    [SET_USERS_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total;
    },
    [UPDATE_PAGINATION](state: Object, payload: Object) {
      state.pagParams.lastFetchCount = payload.count;
      state.pagParams.lastSkip = payload.skip;
      state.pagParams.startRow += 100;
      state.pagParams.endRow += 100;
    },
    // [RESET_CREDENTIALS](state: Object, payload: Object) {

    // },
  },
  actions: {
    [RESET_USERS]: async function ({ commit }: Object) {
      commit(REPLACE_USERS, {});
    },

    [FETCH_USERS]: async function (
      { state, commit, dispatch }: Object,
      payload: Object
    ) {
      try {
        if (state.pagParams.lastFetchCount === 0) {
          return true;
        }

        commit(START_USER_FETCHING, { key: "all" });
        let pathstring = "/users";
        if (payload.orgId) {
          pathstring = "/organizations/" + payload.orgId + "/users";
        }

        const spsfpParms = {
          sortModel: [],
          filterModel: {},
          startRow: state.pagParams.startRow,
          endRow: state.pagParams.endRow,
        };
        const sendToServerParams = computePaginateSortFilterParams(spsfpParms);

        const { data } = await axios.get(pathstring, {
          params: sendToServerParams,
        });
        const { pagination } = data.meta;
        const { entities } = normalize(data);

        commit(ADD_USERS, {
          entities: entities.users,
        });
        commit(UPDATE_PAGINATION, pagination);
        if (pagination.count > 0) {
          commit(ADD_USERS, {
            entities: normalize(data).entities.users,
          });
        }
        commit(SET_USERS_TOTAL, { total: pagination.collection_count });
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: "Failed to load people ",
          detail: err.message,
          color: "error",
        });
      } finally {
        commit(DONE_USER_FETCHING, { key: "all" });
      }
    },
    [FETCH_USER]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      try {
        const { data } = await axios.get(`/users/${payload.id}`);
        const { entities } = normalize(data);
        commit(ADD_USERS, {
          entities: entities.users,
        });
        return { success: true };
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: "Failed to load person ",
          detail: err.message,
          color: "error",
        });
        return { success: false };
      }
    },
    [FETCH_CURRENT_USER]: async function ({
      rootState,
      commit,
      dispatch,
    }: Object) {
      try {
        const currentUserId = rootState.session.user.id;
        if (!currentUserId) {
          throw new Error("No current user id found");
        }
        const { data } = await axios.get(
          `/users/${currentUserId}?include=organization`
        );
        const { entities } = normalize(data);

        commit(UPDATE_SESSION, {
          user: entities.users[currentUserId],
          organization: Object.values(entities.organizations)[0],
        });
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: "Failed to load your user information ",
          detail: err.message,
          color: "error",
        });
      }
    },
    [CREATE_USER]: async function (
      { commit, dispatch }: Object,
      payload: JsonApiEntity
    ) {
      let notificationId;
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Inviting ${
            oPath.get(payload, "attributes.first_name") || "person"
          }...`,
          color: "secondary",
        });
      }

      try {
        const { data } = await axios.post("/users", {
          data: {
            type: "users",
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        });
        const { entities } = normalize(data);
        commit(ADD_USERS, {
          entities: entities.users,
        });
        dispatch("lens_org_states/addUser", {
          id: data.data.attributes.id,
          type: "users",
          attributes: data.data.attributes,
        });
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `${
              oPath.get(payload, "attributes.first_name") || "Person"
            } has been invited!`,
            color: "success",
            undoable: true,
          });
        }
        return data.data.attributes.id;
      } catch (error) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to invite ${
            oPath.get(payload, "attributes.first_name") || "person"
          } `,
          detail: error.message,
          color: "error",
        });
      }
      return false;
    },
    [UPDATE_USER]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      let notificationId;
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: "Saving...",
          undoable: true,
          color: "secondary",
        });
      }

      try {
        const { data } = await axios.patch(`/users/${payload.id}`, {
          data: {
            type: "users",
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        });
        const { entities } = normalize(data);
        commit(MODIFY_USER, entities.users[payload.id]);
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: "Saved!",
            color: "success",
            undoable: true,
          });
        }
        return true;
      } catch (err) {
        let message;
        const errorDetail = oPath.get(err, "response.data.errors.0.detail");
        if (errorDetail.includes("current_password did not match")) {
          message = "Your current password is incorrect";
        } else {
          message = err.message;
        }
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: "Failed to update user ",
          detail: message,
          color: "error",
        });
      }
      return false;
    },
    [DELETE_USER]: async function (
      { state, commit, dispatch }: Object,
      payload: Object
    ) {
      const firstName =
        oPath.get(state.entities[payload.id], "first_name") || "person";
      let notificationId;
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Removing ${firstName} from this organization...`,
          color: "secondary",
        });
      }

      try {
        await axios.delete(`/users/${payload.id}`);
        commit(REMOVE_USER, payload);
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `${firstName} has been removed!`,
            color: "success",
          });
        }
        return true;
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to remove ${firstName} `,
          detail: err.message,
          color: "error",
        });
      }
      return false;
    },
    [USER_FORGOT_PASSWORD]: async function (
      { commit, dispatch }: Object,
      payload: Object
    ) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: "Sending password reset email...",
        color: "secondary",
      });

      try {
        await axios.post(`/users/forgot_password?email=${payload.email}`);
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: "Reset email sent!",
          color: "success",
        });
        return { success: true };
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: "Failed to send password reset email ",
          detail: err.message,
          color: "error",
        });
        return {
          error: `Failed to send password reset email: '${err.message}'`,
        };
      }
    },
    [USER_RESET_PASSWORD]: async function (_: any, payload: Object) {
      try {
        const {
          data: { meta },
        } = await axios.post("/users/reset_password", {
          meta: payload,
        });
        return { success: true, meta };
      } catch (err) {
        return {
          error: `Failed to set password: '${err.message}'`,
        };
      }
    },
    [VERIFY_NEW_USER]: async function (_: any, payload: Object) {
      try {
        const {
          data: { meta },
        } = await axios.post("/users/verify_new_user", {
          data: {
            type: "users",
            attributes: payload,
          },
        });
        return { success: true, meta };
      } catch (err) {
        return {
          error: `Failed to set new user password: '${err.message}'`,
        };
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/users.js